import { inject, Injectable } from '@angular/core';
import {
  ActionConsultantCreationPMColumn,
  CreationPersonneMoraleDashboardColumn,
  DateDemandeCreationPMColumn,
  FormeSocialeCreationPMColumn,
  InvestisseurPhysiqueCreationPMColumn,
  RaisonSocialeCreationPMColumn,
  StatutCreationPMColumn,
} from '@lib/components/creation-personne-morale-dashboard/creation-personne-morale-dashboard-column';
import {
  DeclarationDashboardColumn,
  DeleteDraftDashboardColumn,
  EnveloppeDashboardColumn,
  InvestisseurDashboardColumn,
  MAJDashboardColumn,
  MontantDashboardColumn,
  NatureDashboardColumn,
  OperationDashboardColumn,
  OperationIDDashboardColumn,
  PictoConsultantDashboardColumn,
  ProduitDashboardColumn,
  StatutConsultantDashboardColumn,
} from '@lib/components/operations-dashboard/operations-dashboard-columns';
import { DashboardFilterEnum } from '@lib/models/dashboardFilterEnum';
import { CreationPersonneMoraleStatut, OperationFilterInput } from '@lib/models/generated/graphql';
import { QueryManagerService } from '@lib/services/queryManagerService';
import { gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { CreationPMTab, OperationTab } from 'src/app/components/my-operations/consultant-tab';

export const TO_TREAT_TAB_KEY = 'a-traiter';
export const IN_PROGRESS_TAB_KEY = 'en-cours';
export const HISTORY_TAB_KEY = 'historique';
export const CREATION_PM_TAB_KEY = 'pm';

const isOperationToTreatTab = (statusId: number): boolean => statusId < 100;
const isOperationInProgressTab = (statusId: number): boolean => statusId > 100 && statusId < 400;
const isOperationHistoryTab = (statusId: number): boolean => statusId >= 400;

const tabCounter = gql`
  query ($filterPM: CreationPersonneMoraleFilterInput) {
    toTreatCount: operationsPaginated(where: { statutId: { lt: 100 } }) {
      totalCount
    }
    inProgressCount: operationsPaginated(where: { and: [{ statutId: { gte: 100 } }, { statutId: { lt: 400 } }] }) {
      totalCount
    }
    historiqueCount: operationsPaginated(where: { and: [{ statutId: { gte: 400 } }] }) {
      totalCount
    }
    creationPMCount: creationPersonneMoralesPaginated(where: $filterPM) {
      totalCount
    }
  }
`;
@Injectable({
  providedIn: 'root',
})
export class OperationTabsService {
  queryManager = inject(QueryManagerService);

  toTreatColumnSelection: OperationDashboardColumn[] = [
    new OperationIDDashboardColumn(),
    new InvestisseurDashboardColumn(),
    new NatureDashboardColumn(),
    new ProduitDashboardColumn(),
    new EnveloppeDashboardColumn(),
    new MontantDashboardColumn(),
    new DeclarationDashboardColumn(),
    new MAJDashboardColumn(),
    new StatutConsultantDashboardColumn(),
    new PictoConsultantDashboardColumn(),
    new DeleteDraftDashboardColumn(),
  ];

  inProgressColumnSelection: OperationDashboardColumn[] = [
    new OperationIDDashboardColumn(),
    new InvestisseurDashboardColumn(),
    new NatureDashboardColumn(),
    new ProduitDashboardColumn(),
    new EnveloppeDashboardColumn(),
    new MontantDashboardColumn(),
    new DeclarationDashboardColumn(),
    new MAJDashboardColumn(),
    new StatutConsultantDashboardColumn(),
    new PictoConsultantDashboardColumn(),
  ];

  historiqueColumnSelection: OperationDashboardColumn[] = [
    new OperationIDDashboardColumn(),
    new InvestisseurDashboardColumn(),
    new NatureDashboardColumn(),
    new ProduitDashboardColumn(),
    new EnveloppeDashboardColumn(),
    new MontantDashboardColumn(),
    new DeclarationDashboardColumn(),
    new MAJDashboardColumn(),
    new StatutConsultantDashboardColumn(),
  ];

  creationPMColumnSelection: CreationPersonneMoraleDashboardColumn[] = [
    new RaisonSocialeCreationPMColumn(),
    new FormeSocialeCreationPMColumn(),
    new InvestisseurPhysiqueCreationPMColumn(),
    new DateDemandeCreationPMColumn(),
    new StatutCreationPMColumn(),
    new ActionConsultantCreationPMColumn(),
  ];

  filterSelection: DashboardFilterEnum[] = [
    DashboardFilterEnum.Statut,
    DashboardFilterEnum.Investisseur,
    DashboardFilterEnum.Nature,
    DashboardFilterEnum.Produit,
  ];

  // statut ids strictly lower than 100 cover draft and operation with NC and operation with instance
  toTreatDefaultQueryFilter: OperationFilterInput = {
    statutId: { lt: 100 },
  };

  // statut ids greater than and equal to 100 cover all operation process by back Office - 400 is for done operation
  inProgressDefaultQueryFilter: OperationFilterInput = {
    and: [{ statutId: { gte: 100 } }, { statutId: { lt: 400 } }],
  };

  // statut ids greater than and equal to 400 - opérations terminées
  historiqueDefaultQueryFilter: OperationFilterInput = {
    and: [{ statutId: { gte: 400 } }],
  };

  tabs: (CreationPMTab | OperationTab)[] = [];
  toTreatTab: OperationTab = new OperationTab(
    'A traiter',
    TO_TREAT_TAB_KEY,
    this.toTreatColumnSelection,
    this.filterSelection,
    this.toTreatDefaultQueryFilter
  );
  inProgressTab: OperationTab = new OperationTab(
    'En cours de traitement',
    IN_PROGRESS_TAB_KEY,
    this.inProgressColumnSelection,
    this.filterSelection,
    this.inProgressDefaultQueryFilter
  );

  historiqueTab: OperationTab = new OperationTab(
    'Historique',
    HISTORY_TAB_KEY,
    this.historiqueColumnSelection,
    this.filterSelection,
    this.historiqueDefaultQueryFilter
  );
  creationPMTab: CreationPMTab = new CreationPMTab(
    'Création personne morale',
    CREATION_PM_TAB_KEY,
    this.creationPMColumnSelection
  );

  constructor() {}

  /**
   * Fetches the number of operations in each tab and initializes the tabs
   * with the correct labels and content. If the creationPMCount is greater than
   * 0 or if forceWithPM is true, the tabs will include the creation PM tab.
   * Otherwise, the creation PM tab will be excluded.
   *
   * @param {boolean} forceWithPM - If true, the creation PM tab will be included
   * in the tabs, even if the creationPMCount is 0.
   * @returns {Promise<void>} A promise that resolves when the tabs are initialized.
   */
  async initAndSetTabs(forceWithPM: boolean = false): Promise<void> {
    //  this.activeOperationTab = undefined;
    const withPMs = [this.toTreatTab, this.inProgressTab, this.creationPMTab, this.historiqueTab];
    const withoutPMs = [this.toTreatTab, this.inProgressTab, this.historiqueTab];
    if (forceWithPM) {
      this.tabs = withPMs;
    }
    const requestResult = await firstValueFrom(
      this.queryManager.query<{
        toTreatCount: { totalCount: number };
        inProgressCount: { totalCount: number };
        historiqueCount: { totalCount: number };
        creationPMCount: { totalCount: number };
      }>({
        query: tabCounter,
        variables: {
          filterPM: { statut: { neq: CreationPersonneMoraleStatut.Utilise } },
        },
        fetchPolicy: 'network-only',
      })
    );
    const counter = requestResult.data;
    this.toTreatTab.updateLabel(counter.toTreatCount.totalCount);
    this.inProgressTab.updateLabel(counter.inProgressCount.totalCount);
    this.historiqueTab.updateLabel(counter.historiqueCount.totalCount);
    this.creationPMTab.updateLabel(counter.creationPMCount.totalCount);
    const creationPMCount = counter.creationPMCount.totalCount;

    if (creationPMCount > 0 || forceWithPM) {
      this.tabs = withPMs;
      return;
    }
    this.tabs = withoutPMs;
  }

  getTabByKey(key: string): CreationPMTab | OperationTab | undefined {
    return this.tabs.find(tab => tab.dashboardKey === key);
  }

  getOperationTabKey = (statusId: number): string => {
    if (isOperationToTreatTab(statusId)) {
      return TO_TREAT_TAB_KEY;
    }
    if (isOperationInProgressTab(statusId)) {
      return IN_PROGRESS_TAB_KEY;
    }
    if (isOperationHistoryTab(statusId)) {
      return HISTORY_TAB_KEY;
    }
    return TO_TREAT_TAB_KEY;
  };
}
